import * as React from 'react'
import { cn } from '@/lib/utils'

export interface MenuItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Left icon/decoration element */
  leftIcon?: React.ReactNode
  /** Right icon/decoration element */
  rightIcon?: React.ReactNode
  /** Secondary text line */
  secondaryText?: React.ReactNode
  /** Size variant */
  size?: 'regular' | 'large'
  /** Type variant - destructive shows red text */
  variant?: 'default' | 'destructive'
}

const MenuItem = React.forwardRef<HTMLButtonElement, MenuItemProps>(
  (
    {
      className,
      leftIcon,
      rightIcon,
      secondaryText,
      size = 'regular',
      variant = 'default',
      children,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        className={cn(
          'flex w-full items-center rounded-md transition-colors',
          'hover:bg-border-1 active:bg-border-2',
          'disabled:pointer-events-none disabled:opacity-50',
          size === 'regular'
            ? 'gap-2 px-2 min-h-[32px] py-[5.5px]'
            : 'gap-3 px-3 min-h-[36px] py-2',
          className
        )}
        {...props}
      >
        {leftIcon && (
          <span
            className={cn(
              'shrink-0 [&>svg]:size-5',
              variant === 'destructive' ? 'text-content-negative-bold' : 'text-content-primary-bold'
            )}
          >
            {leftIcon}
          </span>
        )}
        <div className="flex flex-1 flex-col items-start justify-center min-w-0 text-left">
          <span
            className={cn(
              'text-sm leading-normal tracking-[0.07px]',
              size === 'large' && 'font-medium',
              variant === 'destructive' ? 'text-content-negative-bold' : 'text-content-primary-bold'
            )}
          >
            {children}
          </span>
          {secondaryText && (
            <span
              className={cn(
                'leading-normal',
                size === 'regular'
                  ? 'text-sm tracking-[0.07px]'
                  : 'text-xs tracking-[0.18px]',
                variant === 'destructive'
                  ? 'text-content-negative-bold opacity-70'
                  : 'text-content-primary-subtle'
              )}
            >
              {secondaryText}
            </span>
          )}
        </div>
        {rightIcon && (
          <span
            className={cn(
              'shrink-0 [&>svg]:size-5',
              variant === 'destructive' ? 'text-content-negative-bold' : 'text-content-primary-bold'
            )}
          >
            {rightIcon}
          </span>
        )}
      </button>
    )
  }
)

MenuItem.displayName = 'MenuItem'

export { MenuItem }
